import { checkRateLimit, rateLimitResponse } from '~/lib/security.server'
import { Form, useActionData, useNavigation } from 'react-router'
import type { MetaFunction, ActionFunctionArgs } from 'react-router'
import { db } from '@afficheservices/db'
import { users, passwordResetTokens } from '@afficheservices/db/schema'
import { eq, and, isNull } from 'drizzle-orm'
import { sendEmail, emailTemplate } from '@afficheservices/email'
import { Mail, ArrowLeft, CheckCircle } from 'lucide-react'

export const meta: MetaFunction = () => [
  { title: 'Mot de passe oublié — AfficheServices' },
  { name: 'robots', content: 'noindex, nofollow' },
]

function generateToken(): string {
  const bytes = new Uint8Array(32)
  crypto.getRandomValues(bytes)
  return Array.from(bytes).map((b) => b.toString(16).padStart(2, '0')).join('')
}

export async function action({ request }: ActionFunctionArgs) {
  if (!checkRateLimit(request, 'auth')) return rateLimitResponse()
  const formData = await request.formData()
  const email = (formData.get('email') as string)?.trim().toLowerCase()

  if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    return { error: 'Veuillez entrer une adresse email valide' }
  }

  // Always return success (don't reveal if email exists)
  const [user] = await db
    .select({ id: users.id, firstName: users.firstName })
    .from(users)
    .where(and(eq(users.email, email), isNull(users.deletedAt)))
    .limit(1)

  if (user) {
    const token = generateToken()
    const expiresAt = new Date(Date.now() + 60 * 60 * 1000) // 1 hour

    await db.insert(passwordResetTokens).values({
      userId: user.id,
      token,
      expiresAt,
    })

    const resetUrl = `${process.env.APP_URL ?? 'https://www.afficheservices.fr'}/reset-password?token=${token}`

    await sendEmail({
      to: email,
      subject: 'Réinitialisation de votre mot de passe — AfficheServices',
      html: emailTemplate({
        title: 'Réinitialisation du mot de passe',
        body: `
          <p>Bonjour <strong>${user.firstName}</strong>,</p>
          <p>Vous avez demandé la réinitialisation de votre mot de passe.</p>
          <div style="text-align: center; margin: 24px 0;">
            <a href="${resetUrl}" style="display: inline-block; padding: 12px 28px; background: #2563eb; color: white; text-decoration: none; border-radius: 10px; font-weight: 600; font-size: 14px;">
              Réinitialiser mon mot de passe
            </a>
          </div>
          <p style="color: #94a3b8; font-size: 13px; text-align: center;">Ce lien expire dans 1 heure.</p>
          <p style="color: #94a3b8; font-size: 12px;">Si vous n'êtes pas à l'origine de cette demande, ignorez cet email.</p>
        `,
      }),
    }).catch(console.error)
  }

  return { success: true }
}

export default function ForgotPassword() {
  const actionData = useActionData<typeof action>()
  const navigation = useNavigation()
  const isSubmitting = navigation.state === 'submitting'

  if (actionData?.success) {
    return (
      <div className="text-center">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100">
          <CheckCircle className="h-7 w-7 text-emerald-600" />
        </div>
        <h1 className="mt-4 text-xl font-bold text-slate-900">Email envoyé !</h1>
        <p className="mt-2 text-sm text-slate-500">
          Si un compte existe avec cette adresse, vous recevrez un lien de réinitialisation dans quelques minutes.
        </p>
        <p className="mt-4 text-xs text-slate-400">
          Pensez à vérifier votre dossier spam.
        </p>
        <a href="/login" className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-primary hover:underline">
          <ArrowLeft className="h-3.5 w-3.5" /> Retour à la connexion
        </a>
      </div>
    )
  }

  return (
    <div>
      <h1 className="text-2xl font-bold text-slate-900">Mot de passe oublié</h1>
      <p className="mt-2 text-sm text-slate-500">
        Entrez votre email et nous vous enverrons un lien de réinitialisation.
      </p>

      <Form method="post" className="mt-8 space-y-5">
        {actionData?.error && (
          <div className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-600">
            {actionData.error}
          </div>
        )}

        <div>
          <label htmlFor="email" className="block text-sm font-medium text-slate-700">Adresse email</label>
          <div className="relative mt-1">
            <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              required
              placeholder="vous@exemple.fr"
              className="block w-full rounded-xl border border-slate-200 py-3 pl-10 pr-4 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
            />
          </div>
        </div>

        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full rounded-xl bg-primary py-3 text-sm font-semibold text-white hover:bg-primary-hover disabled:opacity-50 btn-press"
        >
          {isSubmitting ? 'Envoi...' : 'Envoyer le lien de réinitialisation'}
        </button>
      </Form>

      <a href="/login" className="mt-6 inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-700">
        <ArrowLeft className="h-3.5 w-3.5" /> Retour à la connexion
      </a>
    </div>
  )
}
